/**
 * Hook for batch-fetching category URLs
 *
 * Given category cards, extracts cards with link_type='category',
 * collects unique category IDs, and fetches their URL paths
 * in a single GraphQL query (prevents N+1).
 */

import { useQuery } from '@tanstack/react-query'

import { homepageApi } from '../../lib'
import { homepageKeys } from '../queryKeys'

import type { HomepageCategoryCard } from '../../types'

/**
 * Fetches category URLs for category cards that link to categories.
 *
 * Builds full URLs from url_path + url_suffix (e.g. "42" → "/rochii.html")
 * matching how product URLs are built (see productHelpers.ts).
 *
 * @returns Map of categoryId → full URL (e.g. "42" → "/rochii.html")
 */
export const useCategoryUrls = (cards: HomepageCategoryCard[]) => {
  // Extract unique category IDs from cards with link_type='category'
  const categoryIds = [
    ...new Set(
      cards
        .filter((card) => card.link_type === 'category' && card.link_value)
        .map((card) => card.link_value)
    ),
  ]

  const { data: categoryUrlMap = new Map<string, string>(), isLoading } = useQuery({
    queryKey: homepageKeys.categoryUrls(categoryIds),
    queryFn: async () => {
      const result = await homepageApi.getCategoryUrls(categoryIds)
      const urlMap = new Map<string, string>()
      for (const category of result.categoryList) {
        const suffix = category.url_suffix ?? '.html'
        urlMap.set(String(category.id), `/${category.url_path}${suffix}`)
      }
      return urlMap
    },
    enabled: categoryIds.length > 0,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })

  return { categoryUrlMap, isLoading }
}
